/** @jsxImportSource @emotion/react */
import { ModalTransition } from 'components/modal-dialog';
import ModalDialog from 'components/modal-dialog/Modal';
import Textfield from 'components/textfield';
import { FormattedMessage } from 'components/i18n';
import { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import Button from '@atlaskit/button';
import React, { useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import { Box, HStack, Stack, VStack } from '@chakra-ui/react';
import ChevronRight from '@atlaskit/icon/glyph/chevron-right';
import { colors } from '@atlaskit/theme';
import SearchIcon from '@atlaskit/icon/glyph/search';
import Spinner from '@atlaskit/spinner';
import { useIntl } from 'react-intl-next';
import { useAuth } from '../../auth';
import api from '../../../libs/api';
import { CompanyAvatar } from '../../../components/avatar';

interface Props {
  isOpen: boolean;
  onCancel: () => void;
  onConnect: (bankCode: string) => void;
}

const ConnectBankAccountModal: React.FC<Props> = ({
  isOpen,
  onCancel,
  onConnect,
}) => {
  const intl = useIntl();
  const { data: banks } = useQuery(['Banks'], async (key) => api.get('Banks', {params: {filter: {where: {'psd2': true } }}}));
  const [isLoading, setIsLoading] = React.useState(false);
  const [selectedBank, setSelectedBank] = React.useState<any>(null);
  const { isBookkeeper } = useAuth();
  const [query, setQuery] = React.useState('');
  const filteredBanks = useMemo(() => {
    console.log('selectedBank', selectedBank);
    if (selectedBank) {
      return [selectedBank];
    }
    return (banks || []).filter((bank) => bank.name.toLowerCase().includes(query.toLowerCase()));

  }, [selectedBank, banks, query]);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setIsLoading(false);
      setSelectedBank(null);
    }
  }, [isOpen]);

  return (
    <ModalTransition>
      {isOpen && (
      <ModalDialog
        onClose={() => onCancel()}
        width="small"
        autoFocus
        shouldScrollInViewport={false}
      >
        <ModalHeader>
          <ModalTitle>
            <Box style={{fontWeight: '600'}}>
              <FormattedMessage id='bank.ConnectBankAccountModal.title' defaultMessage='Connect your bank account' />
            </Box>
          </ModalTitle>
        </ModalHeader>
        <ModalBody>
          <Textfield
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={intl.formatMessage({
              id: 'bank.ConnectBankAccountModal.search',
              defaultMessage: 'Search bank',
            })}
            elemBeforeInput={(
              <Box ml="2" display="flex" alignItems="center" justifyContent="center">
                <SearchIcon size="small" primaryColor={colors.subtleText()} />
              </Box>
          )}
          />
          <VStack mt="4" spacing={0} alignItems="flex-start" ml={-0.5} mr={-0.5}>
            {filteredBanks && filteredBanks.map((bank: any) => (
              <HStack
                onClick={() => {
                  setSelectedBank(bank);
                  setIsLoading(true);
                  onConnect(bank.code);
                }}
                borderRadius={6}
                minHeight={50}
                key={bank.id}
                gap="2"
                pl={1}
                flex={1}
                width="100%"
                _hover={{
                  cursor: 'pointer',
                  backgroundColor: colors.N20,
                }}
              >
                <CompanyAvatar company={bank} />
                <Box fontWeight="600">{bank.name}</Box>
                <Box flex={1} />
                <ChevronRight label="go" size="medium" primaryColor={colors.subtleText()} />
              </HStack>
            ))}
          </VStack>
          {isLoading && <HStack my={3} justifyContent="center"><Spinner /></HStack>}

        </ModalBody>
        <ModalFooter>
          <Button appearance="subtle" onClick={() => onCancel()}>
            <FormattedMessage id='components.modals.delete.cancel' defaultMessage='Cancel' />
          </Button>
        </ModalFooter>
      </ModalDialog>
      )}
    </ModalTransition>
  );
};

export default ConnectBankAccountModal;
